import * as React from "react"
import { Link, navigate } from "gatsby"

import {
  SEARCH_ENTRIES,
  SEARCH_RESULTS_MIN_LENGTH,
  type SearchEntry,
  type SearchDestination,
} from "../../data/search"
import {
  EcontraSignupModal,
  type EcontraSignupModalMode,
} from "../EcontraSignupModal"
import { getUrlFromHref } from "../../utils/externalLinks"

const BRAND = "/figma/shared/brand.png"
const SEARCH_ICON = "/figma/shared/search.svg"
const NAV_LINKS = [
  { href: "/patients/birth-control-101", label: "Birth Control 101" },
  { href: "/patients/products", label: "Our Products" },
  { href: "/patients/resources", label: "Resources" },
] as const

export type SiteHeaderMobileState = "default" | "search-open" | "menu-open"

export type SiteHeaderProps = {
  activePath?: string
  className?: string
  mobileState?: SiteHeaderMobileState
  onMobileStateChange?: (state: SiteHeaderMobileState) => void
  utilityHref?: string
  utilityLabel?: string
}

function joinClasses(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ")
}

function normalizePathname(pathname: string) {
  if (pathname !== "/" && pathname.endsWith("/")) {
    return pathname.slice(0, -1)
  }

  return pathname
}

function buildDestinationHref(destination: SearchDestination) {
  return destination.hash
    ? `${destination.pathname}#${destination.hash}`
    : destination.pathname
}

function MobileMenuToggleIcon({ open }: { open: boolean }) {
  const lineBaseClass = joinClasses(
    "absolute left-1/2 h-[3px] w-[28px] -translate-x-1/2 rounded-full bg-ink-950",
    "transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
    "motion-reduce:transition-none",
  )

  return (
    <span aria-hidden="true" className="relative block h-[37px] w-[37px]">
      <span
        className={joinClasses(
          lineBaseClass,
          open ? "top-1/2 -translate-y-1/2 rotate-45" : "top-[9px] rotate-0",
        )}
      />
      <span
        className={joinClasses(
          lineBaseClass,
          "top-1/2 -translate-y-1/2",
          open ? "scale-x-0 opacity-0" : "opacity-100",
        )}
      />
      <span
        className={joinClasses(
          lineBaseClass,
          open ? "top-1/2 -translate-y-1/2 -rotate-45" : "top-[25px] rotate-0",
        )}
      />
    </span>
  )
}

function SearchResultsPanel({
  entries,
  id,
  labelId,
  onSelect,
}: {
  entries: readonly SearchEntry[]
  id: string
  labelId: string
  onSelect: (destination: SearchDestination) => void
}) {
  if (entries.length === 0) {
    return (
      <div
        aria-labelledby={labelId}
        className="rounded-[22px] border border-ink-950 bg-white px-5 py-4 shadow-[var(--shadow-card)]"
        id={id}
        role="listbox"
      >
        <p className="font-utility text-footnote leading-[1.35] text-ink-950">
          No matches were found
        </p>
      </div>
    )
  }

  return (
    <ul
      aria-labelledby={labelId}
      className="overflow-hidden rounded-[22px] border border-ink-950 bg-white shadow-[var(--shadow-card)]"
      id={id}
      role="listbox"
    >
      {entries.map(entry => (
        <li key={entry.term} role="presentation">
          <button
            aria-selected="false"
            className="flex w-full items-center px-5 py-3 text-left font-utility text-footnote leading-[1.25] text-ink-950 hover:bg-accent-100 focus-visible:bg-accent-100 focus-visible:outline-none"
            onClick={() => onSelect(entry.destination)}
            role="option"
            type="button"
          >
            {entry.displayTerm}
          </button>
        </li>
      ))}
    </ul>
  )
}

export const SiteHeader: React.FC<SiteHeaderProps> = ({
  activePath,
  className,
  mobileState,
  onMobileStateChange,
  utilityHref,
  utilityLabel = "Save on EContra",
}) => {
  const [internalMobileState, setInternalMobileState] =
    React.useState<SiteHeaderMobileState>("default")
  const [query, setQuery] = React.useState("")
  const [isSearchMenuOpen, setIsSearchMenuOpen] = React.useState(false)
  const [isPromoModalOpen, setIsPromoModalOpen] = React.useState(false)
  const [promoModalMode, setPromoModalMode] =
    React.useState<EcontraSignupModalMode>("form")
  const utilityUrl = utilityHref ? getUrlFromHref(utilityHref) : null
  const desktopSearchRef = React.useRef<HTMLDivElement>(null)
  const mobileSearchRef = React.useRef<HTMLDivElement>(null)
  const mobileInputRef = React.useRef<HTMLInputElement>(null)
  const panelId = React.useId()
  const desktopListId = `${panelId}-desktop-search-results`
  const mobileListId = `${panelId}-mobile-search-results`
  const desktopSearchLabelId = `${panelId}-desktop-search-label`
  const mobileSearchLabelId = `${panelId}-mobile-search-label`
  const resolvedMobileState = mobileState ?? internalMobileState
  const normalizedQuery = query.trim().toLowerCase()
  const isMenuOpen = resolvedMobileState === "menu-open"
  const isSearchVisible =
    resolvedMobileState === "search-open" || resolvedMobileState === "menu-open"
  const hasSearchThreshold = normalizedQuery.length >= SEARCH_RESULTS_MIN_LENGTH
  const searchMatches = hasSearchThreshold
    ? [...SEARCH_ENTRIES]
        .filter(entry => entry.term.includes(normalizedQuery))
        .sort((left, right) =>
          left.displayTerm.localeCompare(right.displayTerm, undefined, {
            sensitivity: "base",
          }),
        )
    : []
  const isDropdownVisible = isSearchMenuOpen && hasSearchThreshold

  React.useEffect(() => {
    if (!isMenuOpen || typeof document === "undefined") {
      return undefined
    }

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isMenuOpen])

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return undefined
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      if (
        desktopSearchRef.current?.contains(target) ||
        mobileSearchRef.current?.contains(target)
      ) {
        return
      }

      setIsSearchMenuOpen(false)
    }

    document.addEventListener("pointerdown", handlePointerDown)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [])

  React.useEffect(() => {
    if (!isSearchVisible) {
      setIsSearchMenuOpen(false)
    }
  }, [isSearchVisible])

  React.useEffect(() => {
    if (resolvedMobileState !== "search-open" || typeof window === "undefined") {
      return undefined
    }

    const frameId = window.requestAnimationFrame(() => {
      mobileInputRef.current?.focus()
    })

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [resolvedMobileState])

  function updateMobileState(nextState: SiteHeaderMobileState) {
    onMobileStateChange?.(nextState)

    if (mobileState === undefined) {
      setInternalMobileState(nextState)
    }
  }

  function handleMenuToggle() {
    setIsSearchMenuOpen(false)
    updateMobileState(isMenuOpen ? "default" : "menu-open")
  }

  function handleSearchToggle() {
    if (resolvedMobileState === "search-open") {
      setIsSearchMenuOpen(false)
      updateMobileState("default")
      return
    }

    setIsSearchMenuOpen(hasSearchThreshold)
    updateMobileState("search-open")
  }

  function handleCloseMobileState() {
    setIsSearchMenuOpen(false)
    updateMobileState("default")
  }

  function handleQueryChange(event: React.ChangeEvent<HTMLInputElement>) {
    const nextQuery = event.target.value
    setQuery(nextQuery)
    setIsSearchMenuOpen(
      nextQuery.trim().length >= SEARCH_RESULTS_MIN_LENGTH,
    )
  }

  function handleSearchFocus() {
    if (hasSearchThreshold) {
      setIsSearchMenuOpen(true)
    }
  }

  function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault()
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      setIsSearchMenuOpen(false)
    }
  }

  function handleSearchResultSelect(destination: SearchDestination) {
    setIsSearchMenuOpen(false)
    setQuery("")
    updateMobileState("default")

    if (typeof window === "undefined") {
      void navigate(buildDestinationHref(destination))
      return
    }

    const currentPath = normalizePathname(window.location.pathname)
    const destinationPath = normalizePathname(destination.pathname)

    if (currentPath === destinationPath && destination.hash) {
      const nextHash = `#${destination.hash}`

      if (window.location.hash === nextHash) {
        window.dispatchEvent(new Event("hashchange"))
        return
      }

      window.location.hash = destination.hash
      return
    }

    void navigate(buildDestinationHref(destination))
  }

  function openPromoModal() {
    setIsSearchMenuOpen(false)
    setPromoModalMode("form")
    updateMobileState("default")
    setIsPromoModalOpen(true)
  }

  function closePromoModal() {
    setIsPromoModalOpen(false)
    setPromoModalMode("form")
  }

  function handlePromoSubmit(_data: { email: string; phone: string }) {
    setPromoModalMode("success")
  }

  return (
    <>
      <header
        className={joinClasses("sticky top-0 z-50 w-full", className)}
      >
        <div className="bg-accent-500 text-ink-950">
          <div className="mx-auto flex h-[27px] max-w-[1512px] items-center justify-center px-[18px] lg:h-[42px] lg:px-[65px]">
            {utilityHref ? (
              <a
                className="font-sans text-[13px] font-bold leading-[1.25] tracking-[0.156px] underline lg:text-[15px] lg:tracking-[0.15px]"
                href={utilityHref}
                onClick={handleCloseMobileState}
                rel={utilityUrl ? "noreferrer noopener" : undefined}
                target={utilityUrl ? "_blank" : undefined}
              >
                {utilityLabel}
              </a>
            ) : (
              <button
                className="cursor-pointer appearance-none border-0 bg-transparent p-0 font-sans text-[13px] font-bold leading-[1.25] tracking-[0.156px] underline lg:text-[15px] lg:tracking-[0.15px]"
                onClick={openPromoModal}
                type="button"
              >
                {utilityLabel}
              </button>
            )}
          </div>
        </div>

        <div className="relative bg-white shadow-[0_4px_10px_rgba(0,0,0,0.4)] lg:shadow-card">
          <div className="mx-auto hidden h-20 max-w-[1512px] items-center px-[32px] lg:flex min-[1300px]:px-20">
            <div className="flex min-w-0 items-center gap-[18px] min-[1300px]:w-[975px] min-[1300px]:gap-[10px]">
              <Link className="flex h-[60px] w-[149px] shrink-0 items-center" to="/patients/">
                <img
                  alt="Afaxys Pharma logo"
                  className="h-[60px] w-[128px]"
                  src={BRAND}
                />
              </Link>

              <nav
                aria-label="Primary"
                className="flex min-w-0 items-center gap-[8px] min-[1300px]:flex-1 min-[1300px]:gap-[10px]"
              >
                {NAV_LINKS.map(link => {
                  const isActive = link.href === activePath

                  return (
                    <Link
                      key={link.href}
                      aria-current={isActive ? "page" : undefined}
                      className="group relative flex h-10 shrink-0 items-center justify-center px-4 outline-none min-[1300px]:w-[194px] min-[1300px]:px-0 min-[1368px]:w-[217px]"
                      onClick={handleCloseMobileState}
                      to={link.href}
                    >
                      <span
                        aria-hidden="true"
                        className={joinClasses(
                          "absolute left-0 right-0 top-1/2 h-[50px] -translate-y-1/2 rounded-pill bg-[#d9f3ff] opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 group-active:opacity-100 motion-reduce:transition-none min-[1300px]:left-1/2 min-[1300px]:right-auto min-[1300px]:w-[170px] min-[1300px]:-translate-x-1/2",
                          isActive && "opacity-100",
                        )}
                      />
                      <span
                        className={joinClasses(
                          "relative font-sans text-[16px] font-bold leading-[1.2] text-ink-950 group-hover:underline group-focus-visible:underline group-active:underline",
                          isActive && "underline",
                        )}
                      >
                        {link.label}
                      </span>
                    </Link>
                  )
                })}
              </nav>
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-[12px] min-[1300px]:gap-[10px]">
              <a
                className="flex h-10 w-[110px] shrink-0 items-center justify-center"
                href="/providers"
                rel="noreferrer"
                target="_blank"
              >
                <span className="block w-[96px] text-center font-sans text-[16px] font-normal leading-[1.25] tracking-[-0.176px] text-ink-950 underline">
                  For HCPs
                </span>
              </a>

              <div className="relative w-[235px] shrink-0 min-[1300px]:w-[257px]" ref={desktopSearchRef}>
                <div className="group flex h-[44px] cursor-pointer items-center rounded-pill bg-neutral-200 px-[11px]">
                  <label
                    className="sr-only"
                    htmlFor={`${panelId}-desktop-search-input`}
                    id={desktopSearchLabelId}
                  >
                    Search
                  </label>
                  <input
                    aria-autocomplete="list"
                    aria-controls={desktopListId}
                    aria-expanded={isDropdownVisible}
                    autoComplete="off"
                    className="min-w-0 flex-1 cursor-pointer bg-transparent px-[15px] font-sans text-[14px] font-normal leading-[1.2] text-ink-950 outline-none placeholder:text-ink-950 focus:cursor-text"
                    id={`${panelId}-desktop-search-input`}
                    onChange={handleQueryChange}
                    onFocus={handleSearchFocus}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search"
                    role="combobox"
                    type="text"
                    value={query}
                  />
                  <span
                    aria-hidden="true"
                    className="ml-auto mr-[-4px] mt-[5px] flex h-[35px] w-[35px] shrink-0 items-center justify-center self-start rounded-pill bg-accent-500 transition-colors group-hover:bg-[#8FDCFF] group-focus-within:bg-[#8FDCFF] motion-reduce:transition-none"
                  >
                    <img
                      alt=""
                      aria-hidden="true"
                      className="h-[17.5px] w-[17.5px]"
                      src={SEARCH_ICON}
                    />
                  </span>
                </div>

                {isDropdownVisible ? (
                  <div className="absolute left-0 right-0 top-[calc(100%+10px)] z-20">
                    <SearchResultsPanel
                      entries={searchMatches}
                      id={desktopListId}
                      labelId={desktopSearchLabelId}
                      onSelect={handleSearchResultSelect}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-[1512px] px-[18px] pb-[10px] pt-[15px] lg:hidden">
            <div className="flex h-[56px] items-center justify-between">
              <button
                aria-controls={panelId}
                aria-expanded={isMenuOpen}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                className="flex h-[37px] w-[37px] items-center justify-center"
                onClick={handleMenuToggle}
                type="button"
              >
                <MobileMenuToggleIcon open={isMenuOpen} />
              </button>

              <Link className="flex h-[46px] w-[102px] items-center justify-center" to="/patients/">
                <img
                  alt="Afaxys Pharma logo"
                  className="h-[46px] w-[102px]"
                  src={BRAND}
                />
              </Link>

              <button
                aria-controls={panelId}
                aria-expanded={isSearchVisible}
                aria-label={isSearchVisible ? "Close search" : "Open search"}
                className="flex h-[34px] w-[36.822px] items-center justify-center rounded-pill bg-accent-500"
                onClick={handleSearchToggle}
                type="button"
              >
                <img
                  alt=""
                  aria-hidden="true"
                  className="h-[17.5px] w-[17.5px]"
                  src={SEARCH_ICON}
                />
              </button>
            </div>
          </div>

          <div
            className={joinClasses(
              "absolute left-0 right-0 top-full overflow-hidden bg-white shadow-[0_4px_10px_rgba(0,0,0,0.4)]",
              "transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
              "motion-reduce:transition-none lg:hidden",
              resolvedMobileState === "default"
                ? "pointer-events-none -translate-y-2 opacity-0"
                : "translate-y-0 opacity-100",
            )}
            id={panelId}
          >
            {isSearchVisible ? (
              <div ref={mobileSearchRef}>
                <div className="flex h-[52px] items-center justify-center bg-accent-500">
                  <div className="relative h-[44px] w-[261px] rounded-pill bg-[#f3f3f3] px-[11px]">
                      <label
                        className="sr-only"
                        htmlFor={`${panelId}-mobile-search-input`}
                        id={mobileSearchLabelId}
                      >
                        Search
                      </label>
                      <input
                        aria-autocomplete="list"
                        aria-controls={mobileListId}
                        aria-expanded={isDropdownVisible}
                        autoComplete="off"
                        className="h-full w-full bg-transparent pl-[15px] pr-[56px] font-utility text-[14px] font-normal leading-[1.2] text-ink-950 outline-none placeholder:text-ink-950"
                        id={`${panelId}-mobile-search-input`}
                        onChange={handleQueryChange}
                        onFocus={handleSearchFocus}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="Search"
                        ref={mobileInputRef}
                        role="combobox"
                        type="text"
                        value={query}
                      />
                      <span className="absolute right-[7px] top-[5px] flex h-[34px] w-[36.822px] items-center justify-center rounded-pill bg-[rgba(0,176,255,0.55)]">
                        <img
                          alt=""
                          aria-hidden="true"
                          className="h-[17.5px] w-[17.5px]"
                          src={SEARCH_ICON}
                        />
                      </span>
                  </div>
                </div>

                {isDropdownVisible ? (
                  <div className="bg-accent-500 px-[64.5px] pb-3">
                    <div className="w-[261px]">
                      <SearchResultsPanel
                        entries={searchMatches}
                        id={mobileListId}
                        labelId={mobileSearchLabelId}
                        onSelect={handleSearchResultSelect}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {isMenuOpen ? (
              <div className="overflow-hidden transition-[max-height,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none">
                <div className="min-h-[calc(100vh-160px)] px-[31px] pb-5 pt-5">
                  <nav className="flex w-[347px] flex-col" aria-label="Mobile primary">
                    {NAV_LINKS.map(link => {
                      const isActive = link.href === activePath

                      return (
                        <Link
                          key={link.href}
                          aria-current={isActive ? "page" : undefined}
                          className={joinClasses(
                            "relative -mx-[31px] flex h-[63px] w-screen items-center px-[31px] font-sans text-[18px] font-bold leading-[1.2] text-ink-950 outline-none transition-colors duration-150 ease-out hover:bg-[#d9f3ff] focus-visible:bg-[#d9f3ff] active:bg-[#d9f3ff] motion-reduce:transition-none",
                            isActive && "bg-[#d9f3ff]",
                          )}
                          onClick={handleCloseMobileState}
                          to={link.href}
                        >
                          <span className="w-[347px] border-b border-ink-950 pb-[20px] pt-[20px]">
                            {link.label}
                          </span>
                        </Link>
                      )
                    })}
                  </nav>

                  <a
                    className="mt-[72px] block w-[214px] font-utility text-[16px] font-bold leading-[1.2] text-ink-950"
                    href="/providers"
                    rel="noreferrer"
                    target="_blank"
                  >
                    For HCPs
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <EcontraSignupModal
        isOpen={isPromoModalOpen}
        mode={promoModalMode}
        onClose={closePromoModal}
        onSubmit={handlePromoSubmit}
      />
    </>
  )
}
